import React, { useState, useRef, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    FormLabel,
    RadioGroup,
    Radio,
    FormControlLabel,
    Checkbox,
    Link,
    Alert,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { alpha } from '@mui/material/styles';
import api from '../../services/api';
import { trackEvent } from '../../utils/analytics';

const cyan = '#00BFFF';

const RATINGS = [1, 2, 3, 4, 5];

/**
 * Private feedback modal for ReaderFLEX-published books only.
 *
 * Compliance notes:
 *  - This is NOT an Amazon review. Copy states so explicitly.
 *  - No review gating: the Amazon review button lives on the card and remains
 *    available for every rating. This form never changes availability based on
 *    the star selection.
 *  - Rating is optional. The form works with or without a rating.
 *  - Consent is required before ReaderFLEX may contact the person; if unchecked
 *    the backend records no-contact.
 *  - Star selector is a genuine labeled radio group (keyboard accessible).
 *  - No names, emails, or feedback text are ever sent to analytics.
 */
const FeedbackModal = ({ open, onClose, book }) => {
    const [rating, setRating] = useState('');
    const [feedback, setFeedback] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [consent, setConsent] = useState(false);
    const [company, setCompany] = useState(''); // honeypot
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const feedbackRef = useRef(null);

    useEffect(() => {
        if (open) {
            // Reset transient state each time the modal is opened.
            setError('');
            setSuccess(false);
        }
    }, [open]);

    const resetForm = () => {
        setRating('');
        setFeedback('');
        setName('');
        setEmail('');
        setConsent(false);
        setCompany('');
    };

    const handleClose = () => {
        if (submitting) return;
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!feedback.trim()) {
            setError('Please share a little feedback before sending.');
            if (feedbackRef.current) feedbackRef.current.focus();
            return;
        }
        if (email && !email.includes('@')) {
            setError('Please enter a valid email address or leave it blank.');
            return;
        }

        setSubmitting(true);
        try {
            await api.post('/book-feedback', {
                bookId: book.id,
                bookTitle: book.title,
                rating: rating ? Number(rating) : null,
                feedback: feedback.trim(),
                name: name.trim(),
                email: email.trim(),
                contactConsent: consent,
                company, // honeypot; must stay empty
            });

            // Analytics: non-personal properties only.
            trackEvent('private_feedback_submit', {
                book_id: book.id,
                book_title: book.title,
                has_rating: Boolean(rating),
            });

            resetForm();
            setSuccess(true);
        } catch (err) {
            setError(
                err.response?.data?.error ||
                    'Something went wrong sending your feedback. Please try again in a moment.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            aria-labelledby="feedback-dialog-title"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    border: (theme) => `1px solid ${alpha(cyan, theme.palette.mode === 'dark' ? 0.25 : 0.2)}`,
                },
            }}
        >
            <DialogTitle id="feedback-dialog-title" sx={{ pr: 6, fontWeight: 700 }}>
                Send Private Feedback
                <IconButton
                    aria-label="Close feedback dialog"
                    onClick={handleClose}
                    sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {/* Screen-reader live region for submission status */}
                <Box aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                    {submitting ? 'Sending your feedback' : success ? 'Feedback sent' : ''}
                </Box>

                {success ? (
                    <Box sx={{ py: 2 }}>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Thank you. Your feedback has been sent privately to ReaderFLEX.
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                            Private feedback is sent directly to ReaderFLEX and will not be published as an Amazon review.
                        </Typography>
                    </Box>
                ) : (
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {book?.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Private feedback is sent directly to ReaderFLEX and will not be published as an Amazon review.
                        </Typography>

                        {/* Star rating as a labeled radio group */}
                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                            <FormLabel component="legend" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
                                Optional rating
                            </FormLabel>
                            <RadioGroup
                                row
                                name="book-rating"
                                value={rating}
                                onChange={(e) => setRating(e.target.value)}
                                sx={{ gap: 0.5 }}
                            >
                                {RATINGS.map((value) => {
                                    const selected = Number(rating) >= value && rating !== '';
                                    return (
                                        <FormControlLabel
                                            key={value}
                                            value={String(value)}
                                            sx={{ m: 0 }}
                                            control={
                                                <Radio
                                                    icon={<StarBorderIcon />}
                                                    checkedIcon={<StarIcon />}
                                                    checked={rating === String(value)}
                                                    inputProps={{ 'aria-label': `${value} star${value > 1 ? 's' : ''}` }}
                                                    sx={{
                                                        color: alpha(cyan, 0.5),
                                                        '&.Mui-checked': { color: cyan },
                                                        // Fill stars up to the selected value visually
                                                        ...(selected ? { color: cyan } : {}),
                                                    }}
                                                />
                                            }
                                            label=""
                                        />
                                    );
                                })}
                            </RadioGroup>
                            {rating !== '' && (
                                <Button
                                    onClick={() => setRating('')}
                                    size="small"
                                    sx={{ alignSelf: 'flex-start', mt: 0.5, textTransform: 'none' }}
                                >
                                    Clear rating
                                </Button>
                            )}
                        </FormControl>

                        <TextField
                            inputRef={feedbackRef}
                            label="Your feedback"
                            required
                            multiline
                            minRows={4}
                            fullWidth
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            sx={{ mb: 2 }}
                            inputProps={{ maxLength: 4000 }}
                        />

                        <TextField
                            label="Name (optional)"
                            fullWidth
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            sx={{ mb: 2 }}
                            inputProps={{ maxLength: 120 }}
                        />

                        <TextField
                            label="Email (optional)"
                            type="email"
                            fullWidth
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            sx={{ mb: 1 }}
                            inputProps={{ maxLength: 200 }}
                        />

                        {/* Honeypot: visually hidden, must remain empty */}
                        <Box sx={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
                            <label htmlFor="company-field">Company</label>
                            <input
                                id="company-field"
                                name="company"
                                tabIndex={-1}
                                autoComplete="off"
                                value={company}
                                onChange={(e) => setCompany(e.target.value)}
                            />
                        </Box>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={consent}
                                    onChange={(e) => setConsent(e.target.checked)}
                                    sx={{ color: alpha(cyan, 0.6), '&.Mui-checked': { color: cyan } }}
                                />
                            }
                            label={
                                <Typography variant="body2" color="text.secondary">
                                    ReaderFLEX may contact me about this feedback.
                                </Typography>
                            }
                            sx={{ alignItems: 'flex-start', mt: 1 }}
                        />

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Read our{' '}
                            <Link component={RouterLink} to="/legal/privacy" target="_blank" rel="noopener">
                                Privacy Policy
                            </Link>
                            .
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                {success ? (
                    <Button onClick={handleClose} variant="contained" color="primary">
                        Close
                    </Button>
                ) : (
                    <>
                        <Button onClick={handleClose} color="inherit" disabled={submitting}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            color="primary"
                            disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {submitting ? 'Sending' : 'Send Private Feedback'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default FeedbackModal;
